/**
 * @file history.tab.tsx
 * @description Вкладка истории изменений для формы задачи.
 * Отображает хронологический список изменений статуса задачи 
 * с указанием времени, автора изменения, предыдущего и текущего статуса, а также комментария.
 * Автоматически загружает данные с сервера при отображении вкладки.
 * @author AI Assistant
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react, @src/types, @api/actions, react-hook-form, @consta/uikit/Text, @consta/icons/IconForward, @utils/date-time-helper, @components/icons/user-circle, @components/ui/indicators/state-badge, axios, @utils/error-helper]
 * @related-files [@api/actions/object-form.service.tsx, @components/ui/indicators/state-badge/state-badge.component.tsx, @components/icons/user-circle/user-circle.icon.tsx, @utils/date-time-helper.ts, @utils/error-helper.tsx]
 */

import { tabProps } from '@src/types';
import { ObjectFormService } from '@api/actions';
import { useEffect } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { Text } from '@consta/uikit/Text';
import { IconForward } from '@consta/icons/IconForward';
import { formatTimestamp } from '@utils/date-time-helper';
import { UserCircleIcon } from '@components/icons/user-circle';
import { StateBadge } from '@components/ui/indicators/state-badge';
import axios from 'axios';
import { setErrors } from '@utils/error-helper';

export const History = ({ control, data, tab, getValues, setValue, errorListRef }: tabProps) => {
    useEffect(() => {
        if (getValues && !getValues(tab || 'histories')) {
            getPageData();
        }
    }, [getValues, tab]);

    const getPageData = async () => {
        if (!data?.item) {
            return;
        }

        try {
            const response = await ObjectFormService.get(data.item.$type, data.item.id, tab);

            if (axios.isAxiosError(response)) {
                errorListRef && setErrors(response, errorListRef.current);
                return;
            }

            setValue && setValue(tab || 'histories', response.data);
        } catch (error) {
            console.error('Failed to fetch page data:', error);
        }
    };

    return (
        <Controller
            name={tab || ''}
            control={control}
            render={({ field }: { field: FieldValues }) => (
                <div className="flex flex-col gap-2">
                    {Boolean(field.value?.length) &&
                        field.value?.map((item: any, key: number) => (
                            <div
                                className="border items-center bg-white rounded-[0.8468vmin] p-[1.0585vmin] pb-[0.3177vmin] text-[#002033]"
                                key={key}
                            >
                                <div className="flex gap-default items-center h-[3.4947vmin]">
                                    <Text size="s">{formatTimestamp(item.modifiedAt, 'DD.MM.YYYY HH:mm')}</Text>

                                    <div className="flex items-center">
                                        <Text size="s" className="mr-[1.0585vmin]">
                                            Изменён статус:
                                        </Text>

                                        <StateBadge
                                            state={item.statePrevious}
                                            className="mr-[0.6961vmin] h-[1.9054vmin]"
                                        />

                                        <IconForward size="xs" className="mr-[0.6961vmin]" />

                                        <StateBadge state={item.stateCurrent} className="h-[1.9054vmin]" />
                                    </div>
                                </div>

                                <div className="flex items-center gap-[0.4234vmin]">
                                    <UserCircleIcon />
                                    <Text size="s">{item.modifiedBy?.name}</Text>
                                </div>

                                {item.comment ? (
                                    <Text className="py-[0.7413vmin]" size="s">
                                        {item.comment}
                                    </Text>
                                ) : (
                                    <Text className="py-[0.7413vmin]" size="s" view="ghost">
                                        Нет комментария
                                    </Text>
                                )}
                            </div>
                        ))}
                </div>
            )}
        />
    );
};
